/**
 * tenantTheme — rebrand the admin app's primary ramp to a client's brand color.
 *
 * The Tailwind `primary-*` classes read `--c-primary-<shade>` RGB-channel vars
 * (default = Mavriq gold, in main.css). Here we generate a full 11-shade ramp
 * from a single brand hex and set those vars — recoloring the whole app for a
 * client context (e.g. CO4's team, or a Mavriq user acting-as CO4). Resetting
 * removes the overrides so it falls back to the gold defaults.
 */
const SHADES = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 950] as const

// How far each shade is mixed toward white (<500) or black (>500). Tuned to
// resemble Tailwind's own ramps.
const MIX: Record<number, { toward: 'white' | 'black' | 'base'; t: number }> = {
  50: { toward: 'white', t: 0.92 }, 100: { toward: 'white', t: 0.84 },
  200: { toward: 'white', t: 0.68 }, 300: { toward: 'white', t: 0.46 },
  400: { toward: 'white', t: 0.22 }, 500: { toward: 'base', t: 0 },
  600: { toward: 'black', t: 0.12 }, 700: { toward: 'black', t: 0.28 },
  800: { toward: 'black', t: 0.40 }, 900: { toward: 'black', t: 0.50 },
  950: { toward: 'black', t: 0.72 },
}

function hexToRgb(hex: string): [number, number, number] | null {
  const h = hex.trim().replace('#', '')
  const s = h.length === 3 ? h.split('').map((c) => c + c).join('') : h
  if (!/^[0-9a-fA-F]{6}$/.test(s)) return null
  return [parseInt(s.slice(0, 2), 16), parseInt(s.slice(2, 4), 16), parseInt(s.slice(4, 6), 16)]
}
function mix(a: number[], b: number[], t: number): number[] {
  return a.map((v, i) => Math.round(v + (b[i] - v) * t))
}

export function rampFromHex(hex: string): Record<number, string> | null {
  const base = hexToRgb(hex)
  if (!base) return null
  const white = [255, 255, 255], black = [0, 0, 0]
  const out: Record<number, string> = {}
  for (const s of SHADES) {
    const m = MIX[s]
    const rgb = m.toward === 'base' ? base : mix(base, m.toward === 'white' ? white : black, m.t)
    out[s] = rgb.join(' ')
  }
  return out
}

/** Apply a client's primary color (or null to reset to the Mavriq gold default). */
export function applyPrimaryRamp(hex: string | null): void {
  const root = document.documentElement
  if (!hex) {
    for (const s of SHADES) root.style.removeProperty(`--c-primary-${s}`)
    root.style.removeProperty('--color-primary')
    root.style.removeProperty('--ds-gold')
    return
  }
  const ramp = rampFromHex(hex)
  if (!ramp) return
  for (const s of SHADES) root.style.setProperty(`--c-primary-${s}`, ramp[s])
  // Keep the DS token + hex primary in sync so var(--color-primary)/--ds-gold
  // driven elements rebrand too.
  root.style.setProperty('--color-primary', hex)
  root.style.setProperty('--ds-gold', hex)
}
